/**
 * IssueQrBadgeDialog — reusable QR login badge issuer.
 *
 * Usage:
 *   <IssueQrBadgeDialog
 *     open={open} onClose={close}
 *     userType="student" linkedId="…" tenantId="…"
 *     name="Peter Smith"
 *   />
 *
 * Calls POST /api/auth/qr/issue, renders the returned `qrlogin:*` payload as
 * a scannable QR plus a print-friendly badge, and offers a one-click revoke.
 * The QR exchanges into the user's own JWT on /qr-login.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Printer, RotateCcw, Ban, Copy } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IssueQrBadgeDialog({ open, onClose, userType, linkedId, tenantId, name }) {
  const [busy, setBusy] = useState(false);
  const [payload, setPayload] = useState(null);       // `qrlogin:...`
  const [expiresAt, setExpiresAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { if (!open) { setPayload(null); setError(null); } }, [open]);

  const issue = async (ttlDays) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/qr/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ user_type: userType, linked_id: linkedId, tenant_id: tenantId, ttl_days: ttlDays }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to issue QR');
      setPayload(data.qr_payload);
      setExpiresAt(data.expires_at);
      toast.success('QR badge issued');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke this user\'s QR badge? They will need a new one to scan-login.')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/qr/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ user_type: userType, linked_id: linkedId, tenant_id: tenantId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to revoke');
      toast.success(`Revoked ${data.revoked} token(s)`);
      setPayload(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyPayload = async () => {
    if (!payload) return;
    try { await navigator.clipboard.writeText(payload); toast.success('Copied to clipboard'); }
    catch { toast.error('Unable to copy'); }
  };

  const printBadge = () => {
    const canvas = document.querySelector('#issue-qr-canvas canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const w = window.open('', '_blank', 'width=420,height=620');
    if (!w) return;
    w.document.write(`
      <html><head><title>QR Badge — ${name || 'User'}</title>
      <style>
        body{font-family:system-ui,Segoe UI,Arial;margin:0;padding:24px;display:grid;place-items:center;}
        .badge{width:340px;border:2px solid #111827;border-radius:16px;padding:20px;text-align:center;}
        h1{font-size:16px;margin:0 0 4px;}
        .name{font-size:20px;font-weight:700;margin:6px 0 2px;}
        .meta{font-size:11px;color:#4b5563;margin-bottom:12px;text-transform:uppercase;letter-spacing:0.05em;}
        img{width:240px;height:240px;border:6px solid #f3f4f6;}
        .footer{font-size:10px;color:#6b7280;margin-top:12px;}
      </style></head>
      <body>
        <div class="badge">
          <h1>Scan-to-sign-in</h1>
          <div class="name">${name || 'User'}</div>
          <div class="meta">${userType}</div>
          <img src="${url}" alt="QR" />
          <div class="footer">Tap / scan on any kiosk. Expires ${expiresAt ? new Date(expiresAt).toLocaleDateString() : '—'}.</div>
        </div>
        <script>window.onload=()=>setTimeout(()=>window.print(),100);</script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="issue-qr-dialog">
        <DialogHeader>
          <DialogTitle>Issue QR login badge</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm">
            <div className="text-slate-500">User</div>
            <div className="font-medium">{name || 'Unknown'} <span className="text-xs text-slate-400 ml-1">({userType})</span></div>
          </div>

          {!payload && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">Issuing a new badge rotates any existing one. Short-lived badges reduce risk if lost.</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => issue(1)} disabled={busy} size="sm" variant="outline" data-testid="issue-qr-1day">1 day</Button>
                <Button onClick={() => issue(7)} disabled={busy} size="sm" variant="outline" data-testid="issue-qr-7day">7 days</Button>
                <Button onClick={() => issue(30)} disabled={busy} size="sm" data-testid="issue-qr-30day">
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />} 30 days
                </Button>
                <Button onClick={() => issue(180)} disabled={busy} size="sm" variant="outline" data-testid="issue-qr-180day">6 months</Button>
              </div>
              {error && <p className="text-xs text-rose-600">{error}</p>}
            </div>
          )}

          {payload && (
            <div className="space-y-3">
              <div id="issue-qr-canvas" className="bg-white p-4 rounded-md border grid place-items-center">
                <QRCodeCanvas value={payload} size={220} includeMargin level="M" />
              </div>
              <p className="text-xs text-slate-500 text-center">
                Expires: <span className="font-medium text-slate-700">{expiresAt ? new Date(expiresAt).toLocaleString() : '—'}</span>
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={printBadge} size="sm" data-testid="print-qr-badge">
                  <Printer className="h-4 w-4 mr-1" /> Print badge
                </Button>
                <Button onClick={copyPayload} size="sm" variant="outline" data-testid="copy-qr-payload">
                  <Copy className="h-4 w-4 mr-1" /> Copy token
                </Button>
                <Button onClick={revoke} size="sm" variant="ghost" className="text-rose-600" data-testid="revoke-qr-badge">
                  <Ban className="h-4 w-4 mr-1" /> Revoke
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
